/**
 * The key vault holds the master key that encrypts everything TrueWave stores in
 * this browser. The master key itself is never persisted — it is re-derived on
 * unlock from either a passphrase (PBKDF2) or a passkey (WebAuthn PRF).
 *
 * Derived at unlock:
 *   - `aesKey`   AES-256-GCM, non-extractable — seals every stored record.
 *   - `hmacKey`  HMAC-SHA-256, non-extractable — blind index for lookups by
 *                peer id, so the contact graph is not stored in cleartext.
 */
import { get as kvGet, set as kvSet, del as kvDel, clear as kvClear } from "idb-keyval";
import { bs, fromB64, randomBytes, toB64, utf8, type Bytes } from "../lib/bytes.js";

const VAULT_KEY = "tw.vault";
const HIDDEN_AT_KEY = "tw.hiddenAt";
const PBKDF2_ITERS = 600_000;
const CHECK_PLAINTEXT = "truewave/vault/ok";

type VaultMethod = "passphrase" | "passkey";

interface VaultRecord {
  method: VaultMethod;
  salt: string; // b64
  /** AES-GCM(aesKey, iv||ct) of CHECK_PLAINTEXT — detects a wrong secret. */
  check: string; // b64  (iv[12] || ct)
  /** WebAuthn credential id, for the passkey method. */
  credentialId?: string; // b64
}

let aesKey: CryptoKey | null = null;
let hmacKey: CryptoKey | null = null;

// blindIndex is a pure function of (value, hmacKey), and hmacKey is constant
// for the life of an unlocked session, so its result is safe to memoize.
// Repeated lookups for the same peer (every message list/contact/call-log
// query) are frequent, so this avoids redoing the HMAC each time.
const blindIndexCache = new Map<string, string>();

async function deriveKeys(secret: Bytes, salt: Bytes): Promise<void> {
  const base = await crypto.subtle.importKey("raw", secret, "HKDF", false, ["deriveKey"]);
  aesKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: utf8.encode("tw/aes") },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  hmacKey = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: utf8.encode("tw/hmac") },
    base,
    { name: "HMAC", hash: "SHA-256", length: 256 },
    false,
    ["sign"],
  );
}

async function pbkdf2(passphrase: string, salt: Bytes): Promise<Bytes> {
  const base = await crypto.subtle.importKey(
    "raw",
    utf8.encode(passphrase.normalize("NFKC")),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations: PBKDF2_ITERS },
    base,
    256,
  );
  return new Uint8Array(bits);
}

async function seal(plaintext: Bytes): Promise<Bytes> {
  if (!aesKey) throw new Error("vault locked");
  const iv = randomBytes(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, plaintext));
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv);
  out.set(ct, iv.length);
  return out;
}

async function open(blob: Bytes): Promise<Bytes> {
  if (!aesKey) throw new Error("vault locked");
  const iv = blob.slice(0, 12);
  const ct = blob.slice(12);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv }, aesKey, ct));
}

async function writeCheck(): Promise<string> {
  return toB64(await seal(utf8.encode(CHECK_PLAINTEXT)));
}

async function verifyCheck(check: string): Promise<boolean> {
  try {
    const pt = await open(fromB64(check));
    return utf8.decode(pt) === CHECK_PLAINTEXT;
  } catch {
    return false;
  }
}

export const keyvault = {
  async hasVault(): Promise<boolean> {
    return (await kvGet<VaultRecord>(VAULT_KEY)) !== undefined;
  },

  method(): Promise<VaultMethod | undefined> {
    return kvGet<VaultRecord>(VAULT_KEY).then((v) => v?.method);
  },

  isUnlocked(): boolean {
    return aesKey !== null;
  },

  lock(): void {
    aesKey = null;
    hmacKey = null;
    blindIndexCache.clear();
  },

  async enrollPassphrase(passphrase: string): Promise<void> {
    if (passphrase.length < 8) throw new Error("Passphrase must be at least 8 characters.");
    const salt = randomBytes(16);
    await deriveKeys(await pbkdf2(passphrase, salt), salt);
    const rec: VaultRecord = { method: "passphrase", salt: toB64(salt), check: await writeCheck() };
    await kvSet(VAULT_KEY, rec);
  },

  async unlockPassphrase(passphrase: string): Promise<boolean> {
    const rec = await kvGet<VaultRecord>(VAULT_KEY);
    if (!rec || rec.method !== "passphrase") return false;
    const salt = fromB64(rec.salt);
    await deriveKeys(await pbkdf2(passphrase, salt), salt);
    if (await verifyCheck(rec.check)) return true;
    this.lock();
    return false;
  },

  // --- WebAuthn PRF (passkey) path ------------------------------------------

  async isPrfAvailable(): Promise<boolean> {
    return (
      typeof PublicKeyCredential !== "undefined" &&
      typeof navigator.credentials?.create === "function"
    );
  },

  async enrollPasskey(userId: string, username: string): Promise<void> {
    const salt = randomBytes(16);
    const cred = (await navigator.credentials.create({
      publicKey: {
        challenge: randomBytes(32),
        rp: { name: "TrueWave", id: window.location.hostname },
        user: { id: utf8.encode(userId), name: username, displayName: username },
        pubKeyCredParams: [
          { type: "public-key", alg: -8 },
          { type: "public-key", alg: -7 },
          { type: "public-key", alg: -257 },
        ],
        authenticatorSelection: { residentKey: "required", userVerification: "required" },
        extensions: { prf: { eval: { first: salt } } },
      },
    })) as PublicKeyCredential | null;
    if (!cred) throw new Error("Passkey creation cancelled.");

    const prf = (cred.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } })
      .prf?.results?.first;
    if (!prf) throw new Error("This authenticator does not support PRF. Use a passphrase instead.");

    await deriveKeys(new Uint8Array(prf), salt);
    const rec: VaultRecord = {
      method: "passkey",
      salt: toB64(salt),
      check: await writeCheck(),
      credentialId: toB64(new Uint8Array(cred.rawId)),
    };
    await kvSet(VAULT_KEY, rec);
  },

  async unlockPasskey(): Promise<boolean> {
    const rec = await kvGet<VaultRecord>(VAULT_KEY);
    if (!rec || rec.method !== "passkey" || !rec.credentialId) return false;
    const salt = fromB64(rec.salt);
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        allowCredentials: [{ type: "public-key", id: fromB64(rec.credentialId) }],
        userVerification: "required",
        extensions: { prf: { eval: { first: salt } } },
      },
    })) as PublicKeyCredential | null;
    if (!assertion) return false;

    const prf = (
      assertion.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } }
    ).prf?.results?.first;
    if (!prf) return false;

    await deriveKeys(new Uint8Array(prf), salt);
    if (await verifyCheck(rec.check)) return true;
    this.lock();
    return false;
  },

  // --- record sealing ------------------------------------------------------

  seal,
  open,

  async sealJSON(value: unknown): Promise<Uint8Array> {
    return seal(utf8.encode(JSON.stringify(value)));
  },

  async openJSON<T>(blob: Uint8Array): Promise<T> {
    return JSON.parse(utf8.decode(await open(bs(blob)))) as T;
  },

  /** Deterministic blind index for looking records up by a sensitive value. */
  async blindIndex(value: string): Promise<string> {
    const cached = blindIndexCache.get(value);
    if (cached !== undefined) return cached;
    if (!hmacKey) throw new Error("vault locked");
    const mac = await crypto.subtle.sign("HMAC", hmacKey, utf8.encode(value));
    const index = toB64(new Uint8Array(mac).slice(0, 16));
    blindIndexCache.set(value, index);
    return index;
  },

  /** Destroy the vault and all key material. Caller also clears the database. */
  async wipe(): Promise<void> {
    this.lock();
    await kvDel(VAULT_KEY);
    await kvClear();
  },

  // --- "away" tracking, for the auto-clear-after-being-gone feature --------
  //
  // Deliberately stored unsealed (same as the vault record itself): it's just
  // a timestamp, not content, and it must be writable even while locked (the
  // tab can go hidden with no key in memory) and readable before unlock (the
  // away-check runs at boot, before the user has entered a passphrase).

  /** Call when the tab becomes hidden — records "last seen at". */
  async recordHidden(): Promise<void> {
    await kvSet(HIDDEN_AT_KEY, Date.now());
  },

  /**
   * Call once at boot. Returns how long the app was hidden before this fresh
   * load, or null if there's no recorded gap (first-ever load, or already
   * consumed). Clears the stored value either way — this is a one-shot check
   * per app boot, not a running counter.
   */
  async takeHiddenGapMs(): Promise<number | null> {
    const hiddenAt = await kvGet<number>(HIDDEN_AT_KEY);
    await kvDel(HIDDEN_AT_KEY);
    return typeof hiddenAt === "number" ? Date.now() - hiddenAt : null;
  },
};
